import ApiService from "@/service/api/ApiService";
import { catchError, map } from "rxjs/operators";
import { Observable, of } from "rxjs";
import PageDataModel from "@/service/model/common/PageDataModel";
import TicketModel from "@/service/model/ticket/TicketModel";
import TicketLectureInfoModel from "@/service/model/ticket/TicketLectureInfoModel";

class TicketService extends ApiService {
  /**
   * 티켓 리스트 조회
   * @param page
   * @param itemSize
   * @param organizationId
   * @param status
   * @param keyword
   */
  public retrieveTicketList(
    page: number,
    itemSize: number,
    organizationId?: string,
    status?: string,
    keyword?: string
  ): Observable<PageData<Ticket>> {
    return this.get<PageData<Ticket>>(
      "/ticket/list",
      { page, itemSize, organizationId, status, keyword },
      true
    ).pipe(
      map((ticketPageData: PageData<Ticket>) =>
        PageDataModel.create(ticketPageData)
      ),
      catchError(() => of(new PageDataModel<Ticket>()))
    );
  }

  /**
   * 티켓 리스트 조회
   * @param ticketId
   */
  public retrieveTicketDetail(ticketId: number): Observable<Ticket> {
    return this.get<Ticket>("/ticket", { ticketId }, true).pipe(
      map((ticket: Ticket) => TicketModel.create(ticket)),
      catchError(() => of(new TicketModel()))
    );
  }

  /**
   * 티켓 생성
   * @param title
   * @param organizationId
   * @param lectureIdList
   * @param startedAt
   * @param expiredAt
   */
  public createTicket(
    title: string,
    organizationId: number,
    lectureIdList: number[],
    startedAt: string,
    expiredAt: string
  ): Observable<boolean> {
    return this.post<boolean>(
      "/ticket",
      { title, organizationId, lectureIdList, startedAt, expiredAt },
      true
    ).pipe(
      map((isSuccess: boolean) => isSuccess),
      catchError(() => of(false))
    );
  }

  /**
   * 티켓 수정
   * @param id
   * @param ticket
   * @param lectureIdList
   */
  public modifyTicket(
    ticketId: number,
    ticket: { [key: string]: string | number | null },
    lectureIdList: number[]
  ): Observable<boolean> {
    return this.put<boolean>(
      "/ticket",
      { ticketId, ticket, lectureIdList },
      true
    ).pipe(
      map((isSuccess: boolean) => isSuccess),
      catchError(() => of(false))
    );
  }

  /**
   * 티켓 사용자 조회
   * @param page
   * @param itemSize
   * @param ticketId
   * @param keywordType
   * @param keyword
   */
  public retrieveTicketUserList(
    page: number,
    itemSize: number,
    ticketId: number,
    keywordType?: string,
    keyword?: string
  ): Observable<PageData<TicketUser>> {
    return this.get<PageData<TicketUser>>(
      "/ticket/user/list",
      { page, itemSize, ticketId, keywordType, keyword },
      true
    ).pipe(
      map((ticketUserPageData: PageData<TicketUser>) =>
        PageDataModel.create(ticketUserPageData)
      ),
      catchError(() => of(new PageDataModel<TicketUser>()))
    );
  }

  /**
   * 티켓 전체 사용자 조회
   * @param page
   * @param itemSize
   * @param ticketId
   * @param keyword
   */
  public retrieveAllTicketUserList(
    page: number,
    itemSize: number,
    ticketId: number,
    keyword: string
  ): Observable<PageData<TicketUser>> {
    return this.get<PageData<TicketUser>>(
      "/ticket/user/exclusion",
      { page, itemSize, ticketId, keyword },
      true
    ).pipe(
      map((ticketUserPageData: PageData<TicketUser>) =>
        PageDataModel.create(ticketUserPageData)
      ),
      catchError(() => of(new PageDataModel<TicketUser>()))
    );
  }

  /**
   * 티켓 사용자 등록
   * @param ticketId
   * @param userIdList
   */
  public addedUserToTicket(
    ticketId: number,
    userIdList: string[]
  ): Observable<boolean> {
    return this.post<boolean>(
      "/ticket/user/list",
      { ticketId, userIdList },
      true
    ).pipe(
      map((isSuccess: boolean) => isSuccess),
      catchError(() => of(false))
    );
  }

  /**
   * 티켓 사용자 삭제
   * @param ticketId
   * @param userId
   */
  public deleteUserToTicket(
    ticketId: number,
    userId: number
  ): Observable<boolean> {
    return this.delete<boolean>("/ticket", { ticketId, userId }, true).pipe(
      map((isSuccess: boolean) => isSuccess),
      catchError(() => of(false))
    );
  }

  /**
   * 티켓 렉처 조회
   * @param organizationId
   */
  public getTicketLectureInfoList(
    organizationId: number
  ): Observable<TicketLectureInfo[]> {
    return this.get<TicketLectureInfo[]>(
      "/ticket/lecture/list",
      {
        organizationId
      },
      true
    ).pipe(
      map((list: TicketLectureInfo[]) =>
        list.map(info => TicketLectureInfoModel.create(info))
      ),
      catchError(() => of([]))
    );
  }
}

export const ticketService = new TicketService();
